import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, FileText, Printer, Wallet } from 'lucide-react'

import { financeApi, type FinanceFilters } from '@/services/finance'
import { orgTreeApi } from '@/services/org-tree'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import { formatCurrency, formatDate } from '@/lib/utils'

type DraftWorkEntry = {
  id?: string | number | null
  assignment_ref?: string | null
  workday_entry_ref?: string | null
  work_date?: string | null
  planned_start?: string | null
  planned_end?: string | null
  check_in?: string | null
  check_out?: string | null
  is_late?: boolean
  late_minutes?: number | string | null
  has_overtime?: boolean
  overtime_minutes?: number | string | null
  hours?: string | number | null
  pay_type?: string | null
  base_pay?: string | number | null
  pay_hourly_rate?: string | number | null
  pay_flat?: string | number | null
  additional_pay_details?: Array<{ label?: string | null; amount?: string | number | null; mode?: string | null }>
  total_pay?: string | number | null
  notes?: string | null
}

type DraftPayment = {
  id?: string | number | null
  invoice_id?: string | number | null
  amount?: string | number | null
  payment_method?: string | null
  payment_method_label?: string | null
  paid_on?: string | null
  payment_ref?: string | null
  transaction_id?: string | null
}

type WorkerDraftInvoicePreview = {
  worker_id?: string | number | null
  worker_code?: string | number | null
  worker_name?: string | null
  job_id?: string | number | null
  job_ref?: string | null
  job_title?: string | null
  assignment_id?: string | number | null
  assignment_ref?: string | null
  period_start?: string | null
  period_end?: string | null
  total_earned?: string | number | null
  total_paid?: string | number | null
  pending_amount?: string | number | null
  payments?: DraftPayment[]
  snapshot_document?: {
    worker?: {
      worker_code?: string | number | null
    } | null
    lines?: { work_entries?: DraftWorkEntry[] } | null
    payments?: DraftPayment[]
  } | null
}

const A4_PAGE_CLASS_NAME = 'worker-draft-invoice-a4 mx-auto min-h-[297mm] w-[210mm] max-w-none shrink-0 rounded-sm border border-slate-200 bg-white p-[14mm] text-xs shadow-sm'

const compactText = (...parts: Array<string | number | null | undefined>) =>
  parts.map((part) => String(part ?? '').trim()).filter(Boolean).join(', ')

const readString = (source: unknown, keys: string[]) => {
  if (!source || typeof source !== 'object') return ''
  const record = source as Record<string, unknown>
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

const buildCompanyProfile = (serviceInstance: unknown, parentServiceInstance?: unknown) => {
  const config = serviceInstance && typeof serviceInstance === 'object'
    ? (serviceInstance as Record<string, unknown>).config
    : null
  const parentConfig = parentServiceInstance && typeof parentServiceInstance === 'object'
    ? (parentServiceInstance as Record<string, unknown>).config
    : null
  const configRecord = config && typeof config === 'object' ? config : null
  const parentConfigRecord = parentConfig && typeof parentConfig === 'object' ? parentConfig : null
  const source = serviceInstance && typeof serviceInstance === 'object' ? serviceInstance : {}
  const parentSource = parentServiceInstance && typeof parentServiceInstance === 'object' ? parentServiceInstance : {}

  const name = readString(parentConfigRecord, ['company_name', 'business_name', 'invoice_company_name', 'company_registration_name'])
    || readString(parentSource, ['company_registration_name', 'company_name', 'business_name', 'invoice_company_name', 'name', 'title', 'parent_instance_name'])
    || readString(configRecord, ['company_name', 'business_name', 'invoice_company_name', 'company_registration_name'])
    || readString(source, ['company_registration_name', 'company_name', 'business_name', 'invoice_company_name', 'name', 'title', 'parent_instance_name'])
    || 'Ellan One'
  const logoUrl = readString(configRecord, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'org_logo', 'organization_logo', 'organisation_logo'])
    || readString(source, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'logo_path', 'service_instance_logo', 'org_logo', 'organization_logo', 'organisation_logo', 'app_logo', 'service_logo'])
    || readString(parentConfigRecord, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'org_logo', 'organization_logo', 'organisation_logo'])
    || readString(parentSource, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'logo_path', 'service_instance_logo', 'org_logo', 'organization_logo', 'organisation_logo', 'app_logo', 'service_logo'])
  const branchName = readString(source, ['name', 'title', 'branch_name', 'service_instance_name'])
  const email = readString(configRecord, ['invoice_email', 'branch_email', 'contact_email', 'email'])
    || readString(source, ['email', 'branch_email', 'admin_email', 'branch_admin_email', 'primary_role_email', 'owner_email'])
    || readString(parentSource, ['email', 'admin_email', 'owner_email'])
  const phone = readString(configRecord, ['invoice_phone', 'contact_phone', 'phone', 'phn_nbr'])
    || readString(source, ['phone', 'phn_nbr'])
    || readString(parentSource, ['phone', 'phn_nbr'])
  const address = readString(configRecord, ['invoice_address', 'company_address', 'address'])
    || compactText(
      readString(source, ['address_line_1', 'location']),
      readString(source, ['address_line_2']),
      readString(source, ['city']),
      readString(source, ['postcode', 'zipcode']),
      readString(source, ['country_name']),
    )
    || compactText(
      readString(parentSource, ['address_line_1', 'location']),
      readString(parentSource, ['address_line_2']),
      readString(parentSource, ['city']),
      readString(parentSource, ['postcode', 'zipcode']),
      readString(parentSource, ['country_name']),
    )

  return { name, logoUrl, branchName: branchName && branchName !== name ? branchName : '', email, phone, address }
}

const toNumber = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return 0
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatMoney = (value?: string | number | null) => formatCurrency(toNumber(value))
const formatDateLabel = (value?: string | null) => (value ? formatDate(value) : '-')
const formatTimeLabel = (value?: string | null) => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

const formatMinutesLabel = (value?: string | number | null) => {
  const minutes = Math.round(toNumber(value))
  if (minutes <= 0) return ''
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (hours > 0 && remainingMinutes > 0) return `${hours}h ${remainingMinutes}m`
  if (hours > 0) return `${hours}h`
  return `${remainingMinutes}m`
}

const getEntryRate = (entry: DraftWorkEntry) => {
  const storedRate = entry.pay_type === 'per_hour'
    ? toNumber(entry.pay_hourly_rate || entry.base_pay)
    : entry.pay_type === 'per_job' || entry.pay_type === 'per_day'
    ? toNumber(entry.pay_flat || entry.base_pay)
    : toNumber(entry.base_pay)

  if (storedRate > 0) {
    return storedRate
  }

  const lineBaseAmount = Math.max(toNumber(entry.total_pay) - additionalPayTotal(entry), 0)
  if (entry.pay_type === 'per_hour') {
    const hours = toNumber(entry.hours)
    return hours > 0 ? lineBaseAmount / hours : 0
  }

  return lineBaseAmount
}

const additionalPayTotal = (entry: DraftWorkEntry) => (
  (entry.additional_pay_details ?? []).reduce((sum, item) => sum + toNumber(item.amount), 0)
)

const getQuantityLabel = (entry: DraftWorkEntry) => {
  const hours = entry.hours ?? ''
  if (entry.pay_type === 'per_hour') return hours ? `${hours} hr` : '-'
  if (entry.pay_type === 'per_day') return hours ? `${hours} hr` : '1 day'
  return '1'
}

const getPreviewPayments = (preview?: WorkerDraftInvoicePreview | null) =>
  preview?.payments?.length ? preview.payments : preview?.snapshot_document?.payments ?? []

const formatDraftInvoiceNumber = (preview: WorkerDraftInvoicePreview) => {
  const parts = [
    preview.job_id ? `J${preview.job_id}` : '',
    preview.worker_id ? `W${preview.worker_id}` : '',
    preview.assignment_id ? `A${preview.assignment_id}` : '',
  ].filter(Boolean)
  return parts.length ? `DRAFT-${parts.join('-')}` : 'DRAFT'
}

const buildFiltersFromSearch = (search: string): FinanceFilters => {
  const params = new URLSearchParams(search)
  return {
    service_instance_id: getServiceInstanceIdFromSearch(search),
    job_id: params.get('job_id') || undefined,
    worker_id: params.get('worker_id') || undefined,
    assignment_id: params.get('assignment_id') || undefined,
    date_from: params.get('date_from') || undefined,
    date_to: params.get('date_to') || undefined,
  }
}

export default function WorkerDraftInvoicePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const filters = useMemo(() => buildFiltersFromSearch(location.search), [location.search])
  const returnTo = new URLSearchParams(location.search).get('return_to')

  const query = useQuery<WorkerDraftInvoicePreview>({
    queryKey: ['worker-draft-invoice-page', filters],
    queryFn: () => financeApi.getWorkerDraftInvoice(filters),
    enabled: Boolean(filters.service_instance_id),
    staleTime: 0,
  })

  const serviceInstanceQuery = useQuery({
    queryKey: ['worker-draft-invoice-service-instance', filters.service_instance_id],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(filters.service_instance_id!),
    enabled: Boolean(filters.service_instance_id),
    staleTime: 60_000,
  })

  const parentServiceInstanceQuery = useQuery({
    queryKey: ['worker-draft-invoice-parent-service-instance', filters.service_instance_id],
    queryFn: () => orgTreeApi.findAncestorRoot(filters.service_instance_id!),
    enabled: Boolean(filters.service_instance_id),
    staleTime: 60_000,
  })

  const preview = query.data
  const entries = preview?.snapshot_document?.lines?.work_entries ?? []
  const payments = getPreviewPayments(preview)
  const jobLabel = preview?.job_title || preview?.job_ref || (preview?.job_id ? `Job #${preview.job_id}` : 'All approved work')
  const invoiceNumber = preview ? formatDraftInvoiceNumber(preview) : 'DRAFT'
  const issueDate = formatDateLabel(new Date().toISOString())
  const subtotal = entries.reduce((sum, entry) => sum + toNumber(entry.total_pay), 0)
  const totalAdditionalPay = entries.reduce((sum, entry) => sum + additionalPayTotal(entry), 0)
  const companyProfile = buildCompanyProfile(serviceInstanceQuery.data, parentServiceInstanceQuery.data)
  const workerCode = preview?.worker_code || preview?.snapshot_document?.worker?.worker_code

  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo)
    } else {
      navigate(-1)
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-8">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          body * { visibility: hidden; }
          .worker-draft-invoice-a4, .worker-draft-invoice-a4 * { visibility: visible; }
          .worker-draft-invoice-a4 {
            position: absolute;
            left: 0;
            top: 0;
            width: 210mm !important;
            min-height: 297mm !important;
            margin: 0 !important;
            border: 0 !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          .worker-draft-invoice-page-actions { display: none !important; }
        }
      `}</style>

      <div className="worker-draft-invoice-page-actions sticky top-0 z-20 border-b border-border/70 bg-background/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Button type="button" variant="outline" size="sm" onClick={handleBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-foreground">Draft worker invoice</h1>
              <p className="truncate text-xs text-muted-foreground">A4 preview from approved workday entries and active payments.</p>
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" disabled={!preview} onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
        </div>
      </div>

      <div className="overflow-auto p-4">
        {query.isLoading ? (
          <div className="mx-auto w-[210mm] max-w-full space-y-3">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-96 w-full rounded-xl" />
          </div>
        ) : query.isError ? (
          <div className="mx-auto max-w-2xl rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            Failed to load draft invoice preview.
          </div>
        ) : preview ? (
          <div className="min-w-max">
            <div className={A4_PAGE_CLASS_NAME}>
              <section className="flex flex-col gap-4 border-b-4 border-slate-900 pb-5 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    {companyProfile.logoUrl ? (
                      <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white">
                        <img src={companyProfile.logoUrl} alt={`${companyProfile.name} logo`} className="max-h-full max-w-full object-contain" />
                      </div>
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-sm font-bold text-white">
                        {companyProfile.name.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="text-base font-bold text-slate-950">{companyProfile.name}</p>
                      <p className="text-[11px] text-slate-500">Worker payout draft invoice</p>
                      {companyProfile.branchName ? <p className="text-[11px] text-slate-500">{companyProfile.branchName}</p> : null}
                    </div>
                  </div>
                  <div className="mt-4 max-w-sm text-[11px] leading-5 text-slate-500">
                    {companyProfile.address ? <p>{companyProfile.address}</p> : null}
                    {companyProfile.email || companyProfile.phone ? <p>{compactText(companyProfile.email, companyProfile.phone)}</p> : null}
                    <p>Prepared from approved workday entries and recorded worker payments.</p>
                    <p>This document is a preview and is not posted as a final invoice.</p>
                  </div>
                </div>
                <div className="text-left sm:text-right">
                  <h2 className="text-3xl font-black uppercase tracking-[0.16em] text-slate-950">Invoice</h2>
                  <p className="mt-2 text-slate-500">Invoice No: <span className="font-semibold text-slate-800">{invoiceNumber}</span></p>
                  <p className="text-slate-500">Issue Date: {issueDate}</p>
                  <p className="text-slate-500">Service Period: {formatDateLabel(preview.period_start)}{preview.period_end && preview.period_end !== preview.period_start ? ` - ${formatDateLabel(preview.period_end)}` : ''}</p>
                  <Badge variant="outline" className="mt-2 border-amber-200 bg-amber-50 text-amber-700">Live draft</Badge>
                </div>
              </section>

              <section className="mt-5 grid gap-4 lg:grid-cols-3">
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">From</p>
                  <p className="mt-2 text-sm font-semibold text-slate-950">{companyProfile.name}</p>
                  {companyProfile.branchName ? <p className="mt-1 text-slate-500">{companyProfile.branchName}</p> : null}
                  {companyProfile.address ? <p className="text-slate-500">{companyProfile.address}</p> : null}
                  {companyProfile.email || companyProfile.phone ? <p className="text-slate-500">{compactText(companyProfile.email, companyProfile.phone)}</p> : null}
                  <p className="text-slate-500">Generated by job management</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Pay To</p>
                  <p className="mt-2 text-sm font-semibold text-slate-950">{preview.worker_name || `Worker #${preview.worker_id ?? '-'}`}</p>
                  <p className="mt-1 text-slate-500">Worker Code: {workerCode || '-'}</p>
                  <p className="text-slate-500">Payment recipient: Worker</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Job / Period</p>
                  <p className="mt-2 text-sm font-semibold text-slate-950">{jobLabel}</p>
                  <p className="mt-1 text-slate-500">Job ID: {preview.job_id ?? '-'}</p>
                  <p className="text-slate-500">Assignment: {preview.assignment_ref || '-'}</p>
                </div>
              </section>

              <section className="mt-5">
                <div className="mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-500" />
                  <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-700">Work Entries</h3>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="w-full border-collapse text-xs">
                    <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-[0.12em] text-slate-500">
                      <tr>
                        <th className="w-10 border-b border-slate-200 px-3 py-2">#</th>
                        <th className="border-b border-slate-200 px-3 py-2">Date</th>
                        <th className="border-b border-slate-200 px-3 py-2">Check In</th>
                        <th className="border-b border-slate-200 px-3 py-2">Check Out</th>
                        <th className="border-b border-slate-200 px-3 py-2">Reference</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-right">Qty</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-right">Rate</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-right">Additions</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-right">Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.length ? entries.map((entry, index) => {
                        const entryAdditionalTotal = additionalPayTotal(entry)
                        return (
                          <tr key={`${entry.id ?? 'entry'}-${index}`} className="align-top">
                            <td className="border-b border-slate-100 px-3 py-2 text-slate-500">{index + 1}</td>
                            <td className="border-b border-slate-100 px-3 py-2">{formatDateLabel(entry.work_date)}</td>
                            <td className="border-b border-slate-100 px-3 py-2">
                              <p className="font-medium text-slate-950">{formatTimeLabel(entry.check_in)}</p>
                              {entry.is_late ? (
                                <p className="mt-0.5 text-[11px] font-medium text-amber-700">
                                  Late {formatMinutesLabel(entry.late_minutes)}
                                </p>
                              ) : null}
                            </td>
                            <td className="border-b border-slate-100 px-3 py-2">
                              <p className="font-medium text-slate-950">{formatTimeLabel(entry.check_out)}</p>
                              {entry.has_overtime ? (
                                <p className="mt-0.5 text-[11px] font-medium text-emerald-700">
                                  Overtime {formatMinutesLabel(entry.overtime_minutes)}
                                </p>
                              ) : null}
                            </td>
                            <td className="border-b border-slate-100 px-3 py-2 text-slate-500">
                              <p>{entry.workday_entry_ref || '-'}</p>
                              <p className="text-[11px]">Assignment {entry.assignment_ref || preview.assignment_ref || '-'}</p>
                            </td>
                            <td className="border-b border-slate-100 px-3 py-2 text-right">{getQuantityLabel(entry)}</td>
                            <td className="border-b border-slate-100 px-3 py-2 text-right">{formatMoney(getEntryRate(entry))}</td>
                            <td className="border-b border-slate-100 px-3 py-2 text-right">{entryAdditionalTotal > 0 ? formatMoney(entryAdditionalTotal) : '-'}</td>
                            <td className="border-b border-slate-100 px-3 py-2 text-right font-semibold">{formatMoney(entry.total_pay)}</td>
                          </tr>
                        )
                      }) : (
                        <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">No approved payable workday entries found for this scope.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mt-5 grid gap-4 lg:grid-cols-[1fr_320px]">
                <div className="rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
                    <Wallet className="h-4 w-4 text-slate-500" />
                    <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-700">Payments</h3>
                    <Badge variant="outline" className="ml-auto">{payments.length}</Badge>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {payments.length ? payments.map((payment) => (
                      <div key={String(payment.id)} className="flex items-center justify-between gap-3 px-4 py-2">
                        <div>
                          <p className="font-medium text-slate-950">{payment.payment_method_label || payment.payment_method || 'Payment'}</p>
                          <p className="text-[11px] text-slate-500">
                            {formatDateLabel(payment.paid_on)}
                            {payment.invoice_id ? ` · Invoice #${payment.invoice_id}` : ''}
                            {payment.payment_ref || payment.transaction_id ? ` · ${payment.payment_ref || payment.transaction_id}` : ''}
                          </p>
                        </div>
                        <p className="font-semibold">{formatMoney(payment.amount)}</p>
                      </div>
                    )) : <p className="px-4 py-6 text-center text-slate-500">No payments recorded for this scope.</p>}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200">
                  <table className="w-full text-xs">
                    <tbody>
                      <tr><td className="border-b border-slate-100 px-4 py-2 text-slate-500">Subtotal</td><td className="border-b border-slate-100 px-4 py-2 text-right font-semibold">{formatMoney(subtotal)}</td></tr>
                      <tr><td className="border-b border-slate-100 px-4 py-2 text-slate-500">Included additions</td><td className="border-b border-slate-100 px-4 py-2 text-right font-semibold">{formatMoney(totalAdditionalPay)}</td></tr>
                      <tr><td className="border-b border-slate-100 px-4 py-2 text-slate-500">Total earned</td><td className="border-b border-slate-100 px-4 py-2 text-right font-semibold">{formatMoney(preview.total_earned)}</td></tr>
                      <tr><td className="border-b border-slate-100 px-4 py-2 text-slate-500">Paid</td><td className="border-b border-slate-100 px-4 py-2 text-right font-semibold">{formatMoney(preview.total_paid)}</td></tr>
                      <tr className="bg-amber-50"><td className="px-4 py-3 font-bold text-amber-950">Pending</td><td className="px-4 py-3 text-right text-base font-bold text-amber-950">{formatMoney(preview.pending_amount)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mt-6 grid gap-4 border-t border-slate-200 pt-4 sm:grid-cols-2">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Notes</p>
                  <p className="mt-2 text-[11px] leading-5 text-slate-500">
                    This draft reflects payable approved work entries and active payments at preview time.
                    The final invoice number and status will be assigned when the worker payout invoice is generated.
                  </p>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Prepared For</p>
                  <p className="mt-2 text-sm font-semibold text-slate-950">{preview.worker_name || `Worker #${workerCode || preview.worker_id || '-'}`}</p>
                  <div className="ml-auto mt-6 h-px max-w-48 bg-slate-300" />
                  <p className="mt-1 text-[11px] text-slate-500">Worker acknowledgement</p>
                </div>
              </section>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
